import React, { useEffect, useState } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import {
  fetchMyOrganizerApplication,
  isApproved,
  isSuperadmin,
  applicationStatus,
} from "./organizerApplication.js";

/**
 * Gates /listyourshow/new: only approved organizers (or superadmins) reach the form.
 * Draft / rejected → account setup. Pending → status page on account-setup (no loops).
 */
export default function ApprovedOrganizerRoute() {
  const location = useLocation();
  const [state, setState] = useState({
    loading: true,
    app: null,
    error: "",
    bypass: false,
  });

  useEffect(() => {
    if (isSuperadmin()) {
      setState({ loading: false, app: null, error: "", bypass: true });
      return undefined;
    }

    let active = true;
    setState((prev) => ({ ...prev, loading: true }));
    fetchMyOrganizerApplication()
      .then((app) => {
        if (active) setState({ loading: false, app, error: "", bypass: false });
      })
      .catch((error) => {
        // No application yet → treat as needing setup
        if (error?.status === 404) {
          if (active) setState({ loading: false, app: null, error: "", bypass: false });
          return;
        }
        if (active) {
          setState({
            loading: false,
            app: null,
            error: error.message || "Could not verify organizer status.",
            bypass: false,
          });
        }
      });
    return () => {
      active = false;
    };
  }, [location.key]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#eef1f5] px-4 py-10">
        <div
          className="mx-auto h-56 max-w-4xl animate-pulse rounded-2xl bg-white"
          aria-label="Checking organizer approval"
        />
      </div>
    );
  }

  if (state.bypass) return <Outlet />;

  if (state.error) {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-rose-200 bg-white p-7 text-center shadow-sm">
        <h1 className="text-2xl font-black text-slate-900">Verification unavailable</h1>
        <p className="mt-2 text-sm leading-6 text-slate-600">{state.error}</p>
        <Link
          to="/listyourshow/manage"
          className="mt-6 inline-flex rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-amber-600"
        >
          Back to my events
        </Link>
      </div>
    );
  }

  if (isApproved(state.app)) return <Outlet />;

  const status = applicationStatus(state.app);
  // Pending / draft / rejected / missing → account setup handles display
  return (
    <Navigate
      to="/listyourshow/account-setup"
      replace
      state={{ from: location.pathname, organizerStatus: status }}
    />
  );
}
